import React from 'react';
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from 'framer-motion';
import { FiArrowDownRight, FiArrowUpRight } from 'react-icons/fi';
import { BsGithub, BsLinkedin } from 'react-icons/bs';

import { AppWrap } from '../../wrapper';
import profile from '../../assets/profile-modern.png';
import './Header.scss';

const highlights = [
  { value: '04+', label: 'Years in software engineering' },
  { value: '02', label: 'Peer-reviewed IEEE papers' },
  { value: '90%', label: 'Thesis classifier accuracy' },
];

const Header = () => {
  const reduceMotion = useReducedMotion();
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const smoothX = useSpring(pointerX, { stiffness: 120, damping: 24, mass: 0.35 });
  const smoothY = useSpring(pointerY, { stiffness: 120, damping: 24, mass: 0.35 });
  const portraitX = useTransform(smoothX, [-0.5, 0.5], [-14, 14]);
  const portraitY = useTransform(smoothY, [-0.5, 0.5], [-7, 7]);
  const researchX = useTransform(smoothX, [-0.5, 0.5], [8, -8]);
  const softwareY = useTransform(smoothY, [-0.5, 0.5], [5, -5]);

  const lineVariants = {
    hidden: { y: '112%' },
    visible: { y: 0, transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } },
  };

  const handlePointerMove = (event) => {
    if (reduceMotion) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    pointerX.set((event.clientX - bounds.left) / bounds.width - 0.5);
    pointerY.set((event.clientY - bounds.top) / bounds.height - 0.5);
  };

  const resetPointer = () => {
    pointerX.set(0);
    pointerY.set(0);
  };

  return (
    <div className="app__header">
      <motion.div
        className="hero-shell"
        initial={reduceMotion ? false : { opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: reduceMotion ? 0 : 0.55, ease: [0.22, 1, 0.36, 1] }}
        onPointerMove={handlePointerMove}
        onPointerLeave={resetPointer}
      >
        <div className="hero-shell__meta">
          <p><span /> Software engineer &amp; computer vision researcher</p>
          <p>Dhaka, Bangladesh <span aria-hidden="true">↘</span></p>
        </div>

        <div className="hero-grid">
          <div className="hero-copy">
            <motion.p
              className="hero-kicker"
              initial={reduceMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: reduceMotion ? 0 : 0.82 }}
            >
              Building products. Investigating possibilities.
            </motion.p>

            <motion.h1
              initial={reduceMotion ? false : 'hidden'}
              animate="visible"
              variants={{
                visible: {
                  transition: {
                    delayChildren: reduceMotion ? 0 : 0.72,
                    staggerChildren: reduceMotion ? 0 : 0.09,
                  },
                },
              }}
            >
              <span className="hero-line"><motion.span variants={lineVariants}>Engineering</motion.span></span>
              <span className="hero-line"><motion.span variants={lineVariants}>software.</motion.span></span>
              <span className="hero-line hero-line--accent"><motion.span variants={lineVariants}>Advancing ideas.</motion.span></span>
            </motion.h1>

            <motion.div
              className="hero-summary"
              initial={reduceMotion ? false : { opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: reduceMotion ? 0 : 1.05 }}
            >
              <p>
                I&apos;m <strong>knowaminul</strong>—a software developer with four years of
                industry experience and a computer vision researcher exploring efficient deep
                learning, medical image analysis, and vision transformers.
              </p>
              <div className="hero-actions">
                <a className="button button--acid" href="#work">Explore software <FiArrowDownRight /></a>
                <a className="button button--outline-light" href="#research">View research <FiArrowDownRight /></a>
              </div>
            </motion.div>
          </div>

          <div className="hero-visual" aria-label="Portrait of knowaminul">
            <div className="hero-visual__rings" aria-hidden="true"><span /><span /><span /></div>
            <motion.p className="hero-visual__word" style={{ x: researchX }} aria-hidden="true">
              RESEARCH
            </motion.p>
            <motion.div className="hero-portrait" style={{ x: portraitX, y: portraitY }}>
              <img src={profile} alt="knowaminul" />
            </motion.div>

            <motion.div className="hero-focus-card hero-focus-card--software" style={{ y: softwareY }}>
              <span>01 / Build</span>
              <strong>React · Next.js<br />Sitecore · APIs</strong>
            </motion.div>

            <motion.div className="hero-focus-card hero-focus-card--research" style={{ x: researchX }}>
              <span>02 / Investigate</span>
              <strong>Computer vision<br />Efficient deep learning</strong>
            </motion.div>

            <div className="hero-socials" aria-label="Social links">
              <a href="https://github.com/knowaminul" target="_blank" rel="noreferrer" aria-label="GitHub"><BsGithub /></a>
              <a href="https://www.linkedin.com/in/knowaminul/" target="_blank" rel="noreferrer" aria-label="LinkedIn"><BsLinkedin /></a>
              <a href="/CV_MdAminulIslam.pdf" target="_blank" rel="noreferrer" aria-label="Open academic CV"><FiArrowUpRight /></a>
            </div>
          </div>
        </div>

        <div className="hero-proof" aria-label="Career and research highlights">
          {highlights.map((item, index) => (
            <div key={item.label}>
              <span>0{index + 1}</span>
              <strong>{item.value}</strong>
              <p>{item.label}</p>
            </div>
          ))}
        </div>
      </motion.div>

      <div className="hero-marquee" aria-hidden="true">
        <div>
          <span>SOFTWARE ENGINEERING</span><i>✦</i><span>COMPUTER VISION</span>
          <i>✦</i><span>RESEARCH-DRIVEN THINKING</span><i>✦</i>
          <span>SOFTWARE ENGINEERING</span><i>✦</i><span>COMPUTER VISION</span>
          <i>✦</i><span>RESEARCH-DRIVEN THINKING</span><i>✦</i>
        </div>
      </div>
    </div>
  );
};

export default AppWrap(Header, 'home');
